import { formatFixed } from 'utils/number';
import { DEFAULT_THRESHOLD } from '../utils';
import styles from './delta-indicator.module.scss';

interface DeltaIndicatorProps {
  /** Signed difference, typically `amNri - doNri` (or the WI equivalent). */
  diff: number | null;
  /** Denominator for the percentage — typically `max(|amNri|, |doNri|)`. */
  base: number | null;
  /** Percentage above which the cell turns red. Defaults to `DEFAULT_THRESHOLD`. */
  threshold?: number;
}

const renderPlaceholder = (): JSX.Element => (
  <span aria-label="No data">—</span>
);

/**
 * Formats a numeric diff with a leading `+`/`-` sign and 8-decimal precision.
 * Zero renders as the bare string `'0'` (no sign).
 *
 * @param {number} diff - The signed difference to format.
 * @return {string} Sign-prefixed, comma-grouped, 8-decimal string.
 */
const formatSignedAbsolute = (diff: number): string => {
  if (diff === 0) return '0';
  const sign = diff > 0 ? '+' : '';
  return `${sign}${formatFixed(diff, 8)}`;
};

/**
 * Renders a signed numeric difference + percentage (e.g. `+0.00167000 (1.0%)`)
 * with green/red colour-coding based on the active threshold.
 *
 * Green when the absolute difference is exactly zero **or** the absolute
 * percentage stays at/under `threshold`; red when it exceeds the threshold.
 * Renders an em-dash placeholder when either input is `null` (or when the
 * defensive divide-by-zero guard fires).
 *
 * @param {DeltaIndicatorProps} props - Component props.
 * @return {JSX.Element} A single inline `<span>` carrying the value + colour.
 */
export const DeltaIndicator = ({
  diff,
  base,
  threshold = DEFAULT_THRESHOLD,
}: DeltaIndicatorProps): JSX.Element => {
  if (diff === null || base === null) return renderPlaceholder();

  const absBase = Math.abs(base);
  // Defensive: base = 0 with a non-zero diff would divide by zero. The BE
  // shouldn't produce this (diff is am-do; base is max(|am|, |do|)) — render
  // an em dash if it ever happens.
  if (absBase === 0 && diff !== 0) return renderPlaceholder();

  const percent = absBase === 0 ? 0 : (Math.abs(diff) / absBase) * 100;
  const isWithin = diff === 0 || percent <= threshold;

  return (
    <span className={isWithin ? styles.green : styles.red}>
      {formatSignedAbsolute(diff)} ({formatFixed(percent, 1)}%)
    </span>
  );
};
